import React from 'react';
import type { SxProps, Theme } from '@mui/material';
import {
  Box,
  Card,
  CardContent,
  Divider,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { tokens } from '../../theme';
import { EmptyState } from './EmptyState';

/**
 * A table that stops being a table when there is no room for one.
 *
 * Ten admin screens render raw `<Table>`. A table with six columns cannot fit
 * on a 390px screen: it either scrolls sideways — taking the whole page with
 * it — or squeezes columns until the content is unreadable. Neither is fixable
 * per page without rewriting all ten.
 *
 * So the decision lives here once. At `md` and above this renders a real
 * table. Below it, each row becomes a card: the `title` column is the heading,
 * `subtitle` sits under it, `body` columns become label/value pairs, and
 * `hidden` columns are dropped because a phone has no room for a row's every
 * attribute.
 *
 * Columns declare their own mobile behaviour, so a screen cannot forget to
 * think about it — the default (`body`) is safe rather than clever.
 */
export interface ResponsiveColumn<Row> {
  /** Stable identity for the column. */
  key: string;
  /** Header cell content. Also used as the field label on mobile. */
  header: React.ReactNode;
  /** Cell renderer. Receives the whole row. */
  render: (row: Row) => React.ReactNode;
  align?: 'left' | 'center' | 'right';
  /** Desktop column width. Ignored on mobile. */
  width?: number | string;
  /**
   * How this column appears on a phone.
   *  title    — the card heading (use for the row's name/id)
   *  subtitle — secondary line under the heading
   *  body     — a labelled row in the card body (default)
   *  actions  — pinned to the card footer, label not shown
   *  hidden   — omitted entirely on mobile
   */
  mobile?: 'title' | 'subtitle' | 'body' | 'actions' | 'hidden';
}

interface ResponsiveTableProps<Row> {
  columns: ResponsiveColumn<Row>[];
  rows: Row[];
  /** Stable React key per row. */
  rowKey: (row: Row) => string;
  loading?: boolean;
  /** Shown when there are no rows and loading has finished. */
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: React.ReactNode;
  /** Called when a row is clicked, on either layout. */
  onRowClick?: (row: Row) => void;
  /** Rows to render as skeletons while loading. */
  skeletonRows?: number;
  /**
   * Per-row styling, applied to the table row on desktop and the card on
   * mobile. For flagging a row's state — a duplicate, an overdue item —
   * without the caller reaching into either layout.
   */
  rowSx?: (row: Row) => SxProps<Theme>;
  'aria-label'?: string;
}

export function ResponsiveTable<Row>({
  columns,
  rows,
  rowKey,
  loading = false,
  emptyTitle = 'Nothing here yet',
  emptyDescription,
  emptyAction,
  onRowClick,
  skeletonRows = 5,
  rowSx,
  'aria-label': ariaLabel,
}: ResponsiveTableProps<Row>) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(tokens.mobileBreakpoint));

  const pick = (role: ResponsiveColumn<Row>['mobile']) =>
    columns.filter((c) => (c.mobile ?? 'body') === role);

  if (loading) {
    return (
      <Stack spacing={1.5} sx={{ p: isMobile ? 0 : 2 }}>
        {Array.from({ length: skeletonRows }).map((_, i) => (
          <Skeleton
            key={i}
            variant="rounded"
            height={isMobile ? 104 : 52}
            sx={{ borderRadius: `${tokens.radius.control}px` }}
          />
        ))}
      </Stack>
    );
  }

  if (rows.length === 0) {
    return (
      <EmptyState
        title={emptyTitle}
        description={emptyDescription}
        action={emptyAction}
      />
    );
  }

  // ── Phone: one card per row ───────────────────────────────────────────────
  if (isMobile) {
    const titles = pick('title');
    const subtitles = pick('subtitle');
    const bodies = pick('body');
    const actions = pick('actions');

    return (
      <Stack spacing={1.5} component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {rows.map((row) => (
          <Card
            key={rowKey(row)}
            component="li"
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            sx={{
              cursor: onRowClick ? 'pointer' : 'default',
              '&:active': onRowClick ? { transform: 'scale(0.995)' } : undefined,
              transition: 'transform 80ms ease',
              ...(rowSx?.(row) as object),
            }}
          >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              {titles.map((c) => (
                <Typography key={c.key} variant="subtitle1" sx={{ lineHeight: 1.3 }}>
                  {c.render(row)}
                </Typography>
              ))}
              {subtitles.map((c) => (
                <Typography
                  key={c.key}
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.25 }}
                >
                  {c.render(row)}
                </Typography>
              ))}

              {bodies.length > 0 && (
                <Stack spacing={1} sx={{ mt: 1.5 }}>
                  {bodies.map((c) => (
                    <Box
                      key={c.key}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 2,
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ flexShrink: 0, pt: 0.25 }}
                      >
                        {c.header}
                      </Typography>
                      <Box sx={{ textAlign: 'right', minWidth: 0 }}>{c.render(row)}</Box>
                    </Box>
                  ))}
                </Stack>
              )}

              {actions.length > 0 && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ flexWrap: 'wrap', gap: 1 }}
                    // Actions inside a clickable card must not trigger the row.
                    onClick={(e) => e.stopPropagation()}
                  >
                    {actions.map((c) => (
                      <Box key={c.key}>{c.render(row)}</Box>
                    ))}
                  </Stack>
                </>
              )}
            </CardContent>
          </Card>
        ))}
      </Stack>
    );
  }

  // ── Desktop: a real table ─────────────────────────────────────────────────
  // Every column shows here; `mobile: 'hidden'` only means "no room on a
  // phone", not "not worth showing".
  const visible = columns;

  return (
    <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
      <Table aria-label={ariaLabel} sx={{ minWidth: 640 }}>
        <TableHead>
          <TableRow>
            {visible.map((c) => (
              <TableCell key={c.key} align={c.align ?? 'left'} sx={{ width: c.width }}>
                {c.header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={rowKey(row)}
              hover={Boolean(onRowClick)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              sx={{
                cursor: onRowClick ? 'pointer' : 'default',
                '&:last-child td': { borderBottom: 0 },
                ...(rowSx?.(row) as object),
              }}
            >
              {visible.map((c) => (
                <TableCell key={c.key} align={c.align ?? 'left'}>
                  {c.render(row)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
